import { Pencil, Trash2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { useDnsRecordMutations } from "@/features/dns/hooks/useDns";
import type { IDnsRecord } from "@/features/dns/schemas/dns";

interface DnsRecordsTableProps {
  records: IDnsRecord[];
  isLoading: boolean;
  mutations: ReturnType<typeof useDnsRecordMutations>;
  onEdit: (record: IDnsRecord) => void;
  deleteTarget: IDnsRecord | null;
  onDeleteTargetChange: (record: IDnsRecord | null) => void;
}

export function DnsRecordsTable({
  records,
  isLoading,
  mutations,
  onEdit,
  deleteTarget,
  onDeleteTargetChange,
}: DnsRecordsTableProps) {
  return (
    <>
      <div className="rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Contenido</TableHead>
              <TableHead className="w-20">TTL</TableHead>
              <TableHead className="w-24">Proxy</TableHead>
              <TableHead className="w-28 text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                  Cargando registros…
                </TableCell>
              </TableRow>
            ) : records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                  No hay registros para esta página o filtros.
                </TableCell>
              </TableRow>
            ) : (
              records.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs max-w-[200px] truncate" title={r.name}>
                    {r.name}
                  </TableCell>
                  <TableCell>{r.type}</TableCell>
                  <TableCell className="max-w-[220px] truncate" title={r.content}>
                    {r.content}
                  </TableCell>
                  <TableCell>{r.ttl}</TableCell>
                  <TableCell>{r.proxied ? "Sí" : "No"}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onEdit(r)}
                      aria-label="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => onDeleteTargetChange(r)}
                      aria-label="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => {
          if (!o) onDeleteTargetChange(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar registro DNS</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el registro{" "}
              <span className="font-mono font-medium text-foreground">{deleteTarget?.name}</span> (
              {deleteTarget?.type}). Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={mutations.deleteMut.isPending}
              onClick={() => {
                if (deleteTarget) {
                  mutations.deleteMut.mutate(deleteTarget.id, {
                    onSettled: () => onDeleteTargetChange(null),
                  });
                }
              }}
            >
              {mutations.deleteMut.isPending ? "Eliminando…" : "Eliminar"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
